import { ThumbaIcon } from "@roman/shared/components/Icons";
import LoadingSpinner from "@roman/shared/components/LoadingSpinner";
import { useCountDown } from "@roman/shared/hooks/useCountDown";
import type { RoundDetails } from "@roman/shared/types/RoundDetails";
import { cn } from "@roman/shared/utils";
import { useMemo } from "react";
import { PlayerCard } from "./PlayerCard";
import { MatchName } from "./PlayerCard/components/MatchName";

interface SelectPlayersProps {
  round: RoundDetails;
  setCurrentIndex?: (index: number) => void;
  setPlayerRowIndex?: (index: number) => void;
  setSelectedPlayers?: (fn: (draft: string[]) => void) => void;
  handleSubmit?: () => void;
  currentIndex: number;
  selectedPlayers: string[];
  isSubmitButtonEnable: boolean;
}

export const SelectPlayers = ({
  round,
  selectedPlayers,
  setSelectedPlayers,
  handleSubmit,
  isSubmitButtonEnable,
  setCurrentIndex,
  setPlayerRowIndex,
  currentIndex,
}: SelectPlayersProps) => {
  if (!round) {
    <LoadingSpinner />;
  }

  return (
    <div className="h-screen">
      <MatchName homeName={round.homeName} awayName={round.awayName} roundId={round.id} />
      <div
        className="flex flex-col items-center w-full h-full bg-cover bg-top"
        style={{
          backgroundImage: "url('/assets/soccer-round-background.png')",
        }}
      >
        <RoundInfo
          entryCloseTime={round.entryCloseTime}
          title="活躍する選手はどっち？"
          subTitle="選手カードを押すと選択できます"
        />

        <div className="w-full justify-center items-center px-2">
          <div className="flex gap-1.5 mt-3.5 items-center justify-center">
            <PlayerCard
              player={round.players.homePlayers[currentIndex]}
              currentIndex={currentIndex}
              selectedPlayers={selectedPlayers}
              setCurrentIndex={setCurrentIndex}
              setSelectedPlayers={setSelectedPlayers}
              isSelected={selectedPlayers[currentIndex] === round.players.homePlayers[currentIndex].id}
              roundId={round.id}
            />
            <div className="text-white border-white border rounded-full h-[33px] w-[33px] font-bold z-10 flex justify-center items-center">
              <span className="text-sm font-bold">OR</span>
            </div>
            <PlayerCard
              player={round.players.awayPlayers[currentIndex]}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              selectedPlayers={selectedPlayers}
              setSelectedPlayers={setSelectedPlayers}
              isSelected={selectedPlayers[currentIndex] === round.players.awayPlayers[currentIndex].id}
              roundId={round.id}
            />
          </div>

          <div className="w-full p-4 mt-7 px-5">
            <SelectPlayerRow
              round={round}
              currentIndex={currentIndex}
              setCurrentIndex={setPlayerRowIndex}
              selectedPlayersId={selectedPlayers}
            />
          </div>

          <div className="w-full px-5 flex justify-center items-center">
            <div
              className={cn(
                "w-[370px] font-bold text-lg text-white rounded-lg text-center mb-4 py-[10px] cursor-pointer",
                isSubmitButtonEnable ? "bg-primary" : "bg-disabled",
              )}
              onClick={handleSubmit}
            >
              選手を確定し、キャプテンを選ぶ
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RoundInfo = ({
  entryCloseTime,
  title,
  subTitle,
}: {
  entryCloseTime: number;
  title: string;
  subTitle: string;
}) => {
  return (
    <div className="mt-3 flex flex-col items-center">
      <div className="flex gap-[2px] items-center text-white">
        <SVGComponent />

        <p className="text-sm font-bold">
          <CountDown deadline={entryCloseTime} />
        </p>
      </div>

      <div className="text-white text-center mt-2">
        <p className="text-2xl font-bold">{title}</p>
        <div className="text-[10px] mt-1 font-bold">{subTitle}</div>
      </div>
    </div>
  );
};

const CountDown = ({ deadline }: { deadline: number }) => {
  const { deadlineTime } = useCountDown(new Date(deadline));

  return <span>{deadlineTime}</span>;
};

const SVGComponent = () => (
  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.0791 9.99988V13.4999"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M11.9995 3V6.265" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M11.9993 6.50012C13.6765 6.50089 15.2977 7.10386 16.5678 8.19926C17.838 9.29467 18.6725 10.8097 18.9197 12.4686C19.1668 14.1275 18.8101 15.82 17.9145 17.2381C17.0189 18.6562 15.6438 19.7055 14.0397 20.1952C12.4355 20.6848 10.7089 20.5822 9.17399 19.906C7.63911 19.2298 6.39804 18.025 5.67665 16.5109C4.95527 14.9967 4.80153 13.2739 5.24341 11.6559C5.68529 10.0379 6.69343 8.6324 8.08432 7.69512"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M9.99902 3H13.999" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M18.5444 6.04504L19.4544 6.95504"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type ISelectedPlayerRow = {
  round: RoundDetails;
  currentIndex: number;
  setCurrentIndex?: (index: number) => void;
  selectedPlayersId: string[];
};

const SelectPlayerRow = ({
  currentIndex,
  setCurrentIndex = () => {},
  selectedPlayersId,
  round,
}: ISelectedPlayerRow) => {
  const handleClickPlayer = (index: number) => {
    if (selectedPlayersId[index]) {
      setCurrentIndex(index);
    }
  };
  const allPlayers = [...round.players.homePlayers, ...round.players.awayPlayers];
  const selectedPlayers = selectedPlayersId.map((playerId) => allPlayers.find((player) => player.id === playerId));

  const isSubmitButtonEnable = useMemo(() => selectedPlayersId.every(Boolean), [selectedPlayersId]);

  return (
    <div className="w-full">
      <div className="flex gap-[2px] items-center justify-center box-content">
        {selectedPlayers.map((player, index) => (
          <div
            key={player?.id || index}
            className={cn(
              "relative w-7 h-10 rounded-sm border-2",
              index === currentIndex ? "border-white" : "border-transparent",
            )}
          >
            {index === currentIndex && (
              <div
                className="absolute z-10 w-[10px] h-[8px] bg-white left-1/2 transform -translate-x-[50%] -top-[16px]"
                style={{ clipPath: "polygon(100% 0, 0 0, 50% 100%)" }}
              />
            )}

            <div
              className={`w-full h-full rounded-sm flex items-center justify-center relative cursor-pointer ${
                player ? "overflow-hidden" : "bg-[#1F1C1C] bg-opacity-60"
              }`}
              onClick={() => (player ? handleClickPlayer(index) : undefined)}
            >
              {player ? (
                <img
                  src={`${player?.imageUrl || "/assets/player-1.png"}`}
                  alt="player-1"
                  className="w-full h-full object-cover"
                />
              ) : (
                <>
                  <ThumbaIcon className="w-4 h-4 opacity-10 text-white" />
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className={cn("w-full flex justify-center", !isSubmitButtonEnable && "opacity-0")}>
        <p className={"rounded-full w-[228px] px-4 py-1.5 mt-2 bg-white text-black text-center text-[10px]"}>
          上記のサムネイルを選択して変更できます
        </p>
      </div>
    </div>
  );
};
